import React, { useState, useEffect } from "react";
import { Drawer, Form, Input } from "antd";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import "./style.css";

const ComplianceTemplateDrawer = ({
  visible,
  onClose,
  onSubmit,
  isLoading,
  editMode = false,
  initialValues = null
}) => {
  const [form] = Form.useForm();
  const [formValid, setFormValid] = useState(false);
  const [editorData, setEditorData] = useState("");

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const submitData = {
        template_name: values.template_name,
        template: editorData,
      };
      onSubmit(submitData);
      if (!editMode) {
        form.resetFields();
        setEditorData("");
        setFormValid(false);
      }
    });
  };

  const handleEditorChange = (event, editor) => {
    setEditorData(editor.getData());
  };

  const formValues = Form.useWatch([], form);

  useEffect(() => {
    if (!formValues) return;
    form
      .validateFields({ validateOnly: true })
      .then(() => setFormValid(true))
      .catch(() => setFormValid(false));
  }, [formValues, form, editorData]);

  useEffect(() => {
    if (editMode && initialValues) {
      form.setFieldsValue({ template_name: initialValues.template_name });
      setEditorData(initialValues.template || "");
    } else if (!editMode) {
      form.resetFields();
      setEditorData("");
    }
  }, [editMode, initialValues, form]);

  const editorConfig = {
    toolbar: [
      "heading",
      "|",
      "bold",
      "italic",
      "link",
      "bulletedList",
      "numberedList",
      "|",
      "undo",
      "redo"
    ],
  };

  return (
    <Drawer
      title={editMode ? "Edit Compliance Template" : "Add Compliance Template"}
      placement="right"
      closable
      onClose={() => {
        onClose();
        if (!editMode) {
          form.resetFields();
          setEditorData("");
          setFormValid(false);
        }
      }}
      open={visible}
      width={1000}
      className="compliance-template-drawer"
      footer={
        <div className="drawer-footer">
          <button
            className="cancel-btn"
            onClick={() => {
              onClose();
              if (!editMode) {
                form.resetFields();
                setEditorData("");
                setFormValid(false);
              }
            }}
          >
            Cancel
          </button>
          <button
            className={`submit-btn ${!formValid ? "submit-btn-disabled" : ""}`}
            onClick={handleSubmit}
            disabled={!formValid || isLoading}
          >
            {editMode ? "Update Template" : "Add Template"}
          </button>
        </div>
      }
    >
      <div className="drawer-outer-container">
        <div className="drawer-content-container">
          <Form
            form={form}
            name="compliance-template-form"
            layout="vertical"
            className="compliance-template-form"
          >
            <Form.Item
              name="template_name"
              label="Template Name"
              rules={[{ required: true, message: "Please enter template name!" }]}
            >
              <Input placeholder="Template Name" />
            </Form.Item>

            <div className="template-editor-section">
              <div className="template-variables-info">
                <h4>Available Template Variables:</h4>
                <div className="variables-table">
                  <div className="variable-row">
                    <span className="variable-name">{"{{LICENSEE}}"}</span>
                    <span className="variable-separator">|</span>
                    <span className="variable-description">Name of Partner/company</span>
                  </div>
                  <div className="variable-row">
                    <span className="variable-name">{"{{LICENSEE_ADDRESS}}"}</span>
                    <span className="variable-separator">|</span>
                    <span className="variable-description">Address of Partner/Company</span>
                  </div>
                  <div className="variable-row">
                    <span className="variable-name">{"{{DATE}}"}</span>
                    <span className="variable-separator">|</span>
                    <span className="variable-description">Current Date</span>
                  </div>
                </div>
              </div>

              <div className="editor-container">
                <label className="editor-label">Template Content</label>
                <div className="text-editor">
                  <CKEditor
                    editor={ClassicEditor}
                    data={editorData}
                    config={editorConfig}
                    onChange={handleEditorChange}
                  />
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </Drawer>
  );
};

export default ComplianceTemplateDrawer;
